import { useState, useRef, useEffect, forwardRef } from 'react';
import { createPortal } from 'react-dom';
import { ComboBoxOption } from './ComboBoxOption';
import { useKeyboardNavigation } from '../_hooks/useKeyboardNavigation';
import { useOutsideClick } from '../_hooks/useOutsideClick';
import { useTranslation } from 'react-i18next';

export const ComboBoxDropdown = forwardRef(({ isOpen, options, selectedValue, onSelect, onOpen, onClose, positioning, renderOption, inputRef, anchorId, isMulti = false }, ref) => {
    const { t } = useTranslation();
    
    const [highlightIndex, setHighlightIndex] = useState(0);
    const internalDropdownRef = useRef(null);
    const dropdownRef = ref || internalDropdownRef;
    const listRef = useRef(null);
    const justOpened = useRef(false);

    useEffect(() => {
        if (isOpen && !justOpened.current) {
            justOpened.current = true;
            const selectedIndex = isMulti && Array.isArray(selectedValue)
                ? options.findIndex(opt => selectedValue.includes(opt.value))
                : options.findIndex(opt => opt.value === selectedValue);
            setHighlightIndex(selectedIndex >= 0 ? selectedIndex : 0);
        }
        if (!isOpen) {
            justOpened.current = false;
        }
    }, [isOpen, options, selectedValue, isMulti]);

    const scrollToHighlightRef = useRef(false);
    
    useEffect(() => {
        if (!isOpen || highlightIndex < 0 || !scrollToHighlightRef.current) return;
        
        const highlightedElement = listRef.current?.children[highlightIndex];
        highlightedElement?.scrollIntoView({ block: 'nearest' });
        scrollToHighlightRef.current = false;
    }, [highlightIndex, isOpen]);

    const handleHighlight = (indexOrFn) => {
        scrollToHighlightRef.current = true;
        setHighlightIndex(indexOrFn);
    };

    useKeyboardNavigation(isOpen, options, highlightIndex, onSelect, handleHighlight, onOpen, onClose, inputRef, isMulti);

    useOutsideClick(isOpen, inputRef, dropdownRef, onClose);

    if (!isOpen) return null;

    const { mode, style, usePopover, openUpwards } = positioning;
    const selectedValues = isMulti && Array.isArray(selectedValue) ? new Set(selectedValue) : null;
    const popoverProps = usePopover ? { popover: 'manual', anchor: anchorId } : {};
    const className = ['skynet-combobox-dropdown', `skynet-combobox-dropdown-mode-${mode}`, openUpwards && 'open-upwards'].filter(Boolean).join(' ');

    const dropdownContent = (
        <div
            ref={(node) => {
                internalDropdownRef.current = node;
                if (typeof ref === 'function') {
                    ref(node);
                } else if (ref) {
                    ref.current = node;
                }
            }}
            className={className}
            style={style}
            {...popoverProps}
        >
            {options.length === 0 ? (
                <div className="skynet-combobox-empty">{t('common.noOptionsFound')}</div>
            ) : (
                <div ref={listRef} className="skynet-combobox-options-list">
                    {options.map((option, index) => (
                        <ComboBoxOption
                            key={option.value}
                            option={option}
                            index={index}
                            isHighlighted={index === highlightIndex}
                            isSelected={selectedValues ? selectedValues.has(option.value) : option.value === selectedValue}
                            onSelect={onSelect}
                            onHighlight={handleHighlight}
                            renderOption={renderOption}
                            isMulti={isMulti}
                        />
                    ))}
                </div>
            )}
        </div>
    );

    if (mode === 'portal') {
        return createPortal(dropdownContent, document.body);
    }

    return dropdownContent;
});
